import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import map from "lodash/map";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";

//helpers
import { useDispatch, useSelector } from "react-redux";
import { getPostPreviewModel } from "utils/posts";
import { sortAccounts } from "utils/accounts";

import { getTagTemplates } from "actions/tagTemplates";
import { createGlobalStyle } from "styled-components";
import BugsnagReactReports from "components/BugsnagReactReports/BugsnagReactReports";

//actions
import { selectPlatform } from "actions/postForm";

//selectors
import { useConfirmDialog } from "@/features/post/bulk-create/use-confirm-dialog";
import { getLoading } from "selectors/commonSelectors";

import { PostFormContext, PostFormTemplate } from "@/entities/posts";

import { FormSize, FormThemeProvider } from "ui/ecosystems/forms";
import { Notification } from "@/components/ui/notification";
import { FIELDS } from "constants/PostFormFields";
import { useTrackEvent } from "libs/analytics";
import { useAllAccounts } from "@/entities/accounts";
import { useClickOutside } from "libs/use-click-outside";

import { shouldIgnoreClick } from "./utils";
import { ClusterPostForm } from "./components/cluster-post-form";
import { SinglePlatformPostForm } from "./components/single-post-form";
import {
  AccountMultiSelectDropdown,
  accountTypesToSelectorAccounts,
  accountTypesToSelectorGroups,
} from "@/components/ui/selectors";
import { COPY_TO_DRAFT_ADD_ACCOUNT } from "constants/Events";
import { POST_TYPES } from "../../../constants/PostsView";
import { useTikTok } from "./TiktokFields/useTikTok";
import { useLabels } from "./components/labels-hooks";
import { useThreadsFields } from "./threadsFields/useThreadsFields";
import { Post, PostForm as PostFormType } from "../../../shared/types/post";
import {
  AccountPlatformType,
  AccountType,
} from "../../../shared/types/accounts";
import { BulkTabContext, FormContextValue } from "./types";
import { setCoverPreviewUrl } from "../../../features/post/bulk-create/bulk-create-post.actions";
import { useSharedHistoryMap } from "../../../ui/SkedCaption/v2/use-shared-history";
import { FlushRegistryContext } from "./platform-fields/flush-registry";
import {
  filterPostFormPickerAccounts,
  resolvePostFormPickerSelection,
} from "./account-selector-adapter";
import { getPreviewAccounts } from "./preview-accounts";

// Global styles for PostForm tooltips - moved outside component to avoid recreation on every render
const PostFormTooltipGlobalStyles = createGlobalStyle`
  .modal-dialog {
    [data-radix-popper-content-wrapper] {
      font-size: 12px;
      font-family: Inter;
      font-style: normal;
      font-weight: 500; // figma shows 600 but too bold if we use 600

      a {
        color: #ffffff !important;
        text-decoration-line: underline;
      }
    }
  }
`;

const CLIENT_ONLY_COVER_FIELDS = [
  FIELDS.VIDEO_COVER_PREVIEW,
  FIELDS.VIDEO_COVER_ORIGINAL,
  FIELDS.VIDEO_COVER_ORIGINAL_TIMESTAMP,
  FIELDS.VIDEO_COVER_ORIGINAL_TYPE,
  FIELDS.VIDEO_COVER_PREVIEW_SOURCE,
];

function comparablePostFormValue(post: Post | null) {
  return post ? omit(post, CLIENT_ONLY_COVER_FIELDS) : post;
}

function PostForm(
  props: PostFormType & {
    formTitle?: string;
    setIsFormValueChanged: (isChanged: boolean) => void;
    onCancel: () => void;
    accountEditingIsDisabled?: boolean;
    bulkTab?: BulkTabContext;
  }
) {
  const dispatch = useDispatch();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const trackEvent = useTrackEvent();
  const allAccounts = useAllAccounts();
  const [captionHistoryMap] = useSharedHistoryMap();

  useEffect(() => {
    dispatch(getTagTemplates());
  }, []);

  const isLoading = useSelector(getLoading);
  const sortedAccounts = useMemo(
    () => sortAccounts(Object.values(props.formAccounts || [])),
    [props.formAccounts]
  );
  const selectedAccountIds = map(sortedAccounts || [], "_id");
  const previewAccounts = useMemo(
    () => getPreviewAccounts(sortedAccounts, props.selectedPlatform),
    [props.selectedPlatform, sortedAccounts]
  );
  const [previewAccountId, setPreviewAccountId] = useState(
    () => previewAccounts[0]?._id ?? ""
  );
  const effectivePreviewAccountId = previewAccounts.some(
    (account) => account._id === previewAccountId
  )
    ? previewAccountId
    : (previewAccounts[0]?._id ?? "");

  useEffect(() => {
    if (previewAccountId !== effectivePreviewAccountId) {
      setPreviewAccountId(effectivePreviewAccountId);
    }
  }, [effectivePreviewAccountId, previewAccountId]);

  // Obj passed to the post preview
  const postModel = useMemo(
    () =>
      previewAccounts.length > 0
        ? getPostPreviewModel(
            { ...props.platformPost },
            previewAccounts,
            props.selectedPlatform,
            effectivePreviewAccountId
          )
        : null,
    [
      effectivePreviewAccountId,
      previewAccounts,
      props.platformPost,
      props.selectedPlatform,
    ]
  );

  const [initialPostFormValue, setInitialPostFormValue] = useState<Post | null>(
    null
  );

  const {
    isFetchingAccountGroups,
    selectedAccountGroupId,
    selectedContentLabel,
    contentLabelOptions,
    isContentLabelFeatureEnabled,
  } = useLabels({ postFormValue: props.postFormValue });

  // for edit post
  useEffect(() => {
    if (!initialPostFormValue && props.postFormValue) {
      setInitialPostFormValue(props.postFormValue);
    } else {
      props.setIsFormValueChanged(
        !isEqual(
          comparablePostFormValue(initialPostFormValue),
          comparablePostFormValue(props.postFormValue)
        )
      );
    }
  }, [props.postFormValue]);

  // Flush registry: per-platform debounced field editors register their
  // `.flush()` here so we can synchronously commit any in-flight edit BEFORE a
  // remount-causing dispatch (platform switch / customise toggle / story
  // toggle), while the OLD selectedPlatform is still active.
  const pendingFlushesRef = useRef<Set<() => void>>(new Set());

  const registerFlush = useCallback((fn: () => void) => {
    pendingFlushesRef.current.add(fn);
    return () => {
      pendingFlushesRef.current.delete(fn);
    };
  }, []);

  const flushPending = useCallback(() => {
    pendingFlushesRef.current.forEach((fn) => {
      try {
        fn();
      } catch {
        /* ignore */
      }
    });
  }, []);

  // Stable provider value so editors don't churn (re)registering every render.
  const flushRegistryValue = useMemo(
    () => ({ register: registerFlush, flushPending }),
    [registerFlush, flushPending]
  );

  const handlesSelectPlatform = useCallback(
    (platform: AccountPlatformType) => {
      // Commit any in-flight per-platform edit before the platform switch
      // remounts the field editors (and cancels their debounces).
      flushPending();
      dispatch(
        selectPlatform(platform, props.bulkTab ? props.post._id : undefined)
      );
    },
    [dispatch, flushPending]
  );

  const confirmDiscardOverride = useCallback(
    (onConfirm: () => void) => {
      confirm({
        title: "Discard custom content?",
        message:
          "This platform has custom content that will be replaced with the shared post content.",
        confirmText: "Discard",
        cancelText: "Keep editing",
        confirmVariant: "red-primary",
        onConfirm,
      });
    },
    [confirm]
  );

  const isSinglePlatform = props.platforms?.length <= 1;

  const ref = useRef(null);
  const mediaControlsDisabled = isLoading || Boolean(props.post.past);
  const allDisabled = sortedAccounts.length === 0 || mediaControlsDisabled;

  const formContextValue = useMemo(
    () =>
      ({
        post: props.post,
        postFormValue: props.postFormValue,
        platformPost: props.platformPost,
        originalPostState: props.originalPostState,
        allDisabled,
        mediaControlsDisabled,
        updateField: props.updateField,
        updateFields: props.updateFields,
        selectPlatform: handlesSelectPlatform,
        selectedPlatform: props.selectedPlatform,
        platforms: props.platforms,
        errors: props.errors,
        warnings: props.warnings,
        postState: props.postState,
        channelId: props.channelId,
        onCancel: props.onCancel,
        onSubmit: props.onSubmit,
        saveDisabled: props.saveDisabled,
        isSubmitting: props.isSubmitting,
        isSummary: props.isSummary,
        selectedAccountIds,
        previewAccounts,
        previewAccountId: effectivePreviewAccountId,
        setPreviewAccountId,
        postModel,
        flushPending,
        confirmDiscardOverride,
      }) as FormContextValue,
    [
      props.post,
      props.postFormValue,
      props.platformPost,
      props.originalPostState,
      props.updateField,
      props.updateFields,
      props.platforms,
      props.selectedPlatform,
      props.errors,
      props.warnings,
      props.postState,
      props.channelId,
      props.onCancel,
      props.onSubmit,
      props.saveDisabled,
      props.isSubmitting,
      props.isSummary,
      allDisabled,
      mediaControlsDisabled,
      handlesSelectPlatform,
      selectedAccountIds,
      previewAccounts,
      effectivePreviewAccountId,
      postModel,
      flushPending,
      confirmDiscardOverride,
    ]
  );

  const accountPickerProps = {
    accounts: allAccounts,
    selectedAccountIds: selectedAccountIds,
    isMulti: true as const,
    onChange: (selectedAccounts: AccountType[]) => {
      if (!props.post.failedAt && !props.post.originalPostState) {
        trackEvent({ eventName: COPY_TO_DRAFT_ADD_ACCOUNT });
      }
      // NOTE 3 argument use debounce or not
      props.updateField(FIELDS.ACCOUNTS, selectedAccounts);
    },
  };

  const onClusterChange = async (selectedAccounts: AccountType[]) => {
    if (selectedAccounts && sortedAccounts.length > selectedAccounts.length) {
      const platform = sortedAccounts.find(
        // eslint-disable-next-line unicorn/prefer-array-some
        (account) => !selectedAccounts.find((item) => account._id === item._id)
      ) as AccountType;
      const isLastPlatform =
        sortedAccounts.filter(
          (item) => item.platformName === platform.platformName
        ).length === 1;
      const platformType = platform.platformType || "IG";
      const hasUnlinked =
        props.platformFields[platformType] &&
        props.platformFields[platformType].unlinked;

      if (hasUnlinked && isLastPlatform) {
        confirm({
          title: "Cancel creating post",
          message: `Are you sure you want to cancel editing this ${platform.platformName} post? Any unsaved work will be lost. You will be able to continue editing posts on the other selected platforms.`,
          confirmText: "Remove",
          cancelText: "Cancel",
          icon: "fa-solid fa-triangle-exclamation",
          iconBg: "bg-yellow-100",
          iconColor: "text-yellow-600",
          onConfirm: () => {
            // NOTE 3 argument use debounce or not
            props.updateField(FIELDS.ACCOUNTS, selectedAccounts);
          },
        });
        return;
      }
    }
    // NOTE 3 argument use debounce or not
    props.updateField(FIELDS.ACCOUNTS, selectedAccounts);
  };

  // When user clicks outside from PostForm we shows him confirmation window.
  // In bulk create mode, disable entirely — clicks outside are normal navigation.
  useClickOutside(
    ref,
    (event: MouseEvent | TouchEvent) => {
      if (!shouldIgnoreClick(event as MouseEvent)) {
        props.onCancel();
      }
    },
    { enabled: !props.bulkTab }
  );

  const renderAccountsSelector = (pickerProps?: {
    onChange?: (selectedAccounts: AccountType[]) => void;
  }) => {
    const pickerPlatforms = formContextValue.postFormValue?.isStory
      ? (["IG"] as AccountPlatformType[])
      : isSinglePlatform
        ? ([] as AccountPlatformType[])
        : undefined;
    const resolvedOnChange = (
      isSinglePlatform
        ? (pickerProps?.onChange ?? accountPickerProps.onChange)
        : onClusterChange
    ) as (selectedAccounts: AccountType[]) => void;
    const pickerAccounts = filterPostFormPickerAccounts(
      allAccounts,
      pickerPlatforms
    );
    const selectorAccounts = accountTypesToSelectorAccounts(pickerAccounts);
    const selectorGroups = accountTypesToSelectorGroups(pickerAccounts);

    return (
      <>
        <AccountMultiSelectDropdown
          accounts={selectorAccounts}
          groups={selectorGroups}
          selectedIds={selectedAccountIds}
          onChange={(ids) =>
            resolvedOnChange(
              resolvePostFormPickerSelection(pickerAccounts, ids)
            )
          }
          disabled={props.accountEditingIsDisabled || props.post.past}
          placeholder="Select accounts..."
          searchable
          clearable
          maxVisibleAccounts={4}
          aria-label="Post accounts"
        />
        {selectedAccountIds.length === 0 && (
          <Notification
            className="mt-2"
            variant="error"
            title="Please select at least one account."
          />
        )}
      </>
    );
  };

  const {
    oldTiktokAccounts,
    ttIsNotification,
    showUrlField,
    hasTikTokPosting,
  } = useTikTok({
    sortedAccounts,
    platformFormValue: formContextValue.platformPost,
    visibleFields: props.visibleFields,
    selectedPlatform: props.selectedPlatform,
    updateField: props.updateField,
  });

  const { oldThreadsAccounts, thIsNotification, hasThreadsPosting } =
    useThreadsFields({
      sortedAccounts,
      platformFormValue: formContextValue.platformPost,
      visibleFields: props.visibleFields,
      selectedPlatform: props.selectedPlatform,
      updateField: props.updateField,
    });

  const hasPostTags = useMemo(() => {
    const isVideoType =
      formContextValue.postFormValue?.type === POST_TYPES.VIDEO;
    const hasTagsVisibleFields = [
      FIELDS.LOCATION_TAG,
      FIELDS.USER_TAGS,
      FIELDS.PRODUCT_TAGS,
      FIELDS.ALT_TEXT,
      FIELDS.COLLABORATOR_TAG,
    ].some((tagField) => props.visibleFields?.includes(tagField));

    return !isVideoType && hasTagsVisibleFields;
  }, [formContextValue.postFormValue?.type, props.visibleFields]);

  const handleCoverPreviewChange = (coverUrl: string | null) => {
    if (props.bulkTab) {
      dispatch(
        setCoverPreviewUrl({
          id: props.bulkTab.formId,
          url: coverUrl,
        })
      );
    }
  };

  return (
    <BugsnagReactReports>
      <FormThemeProvider size={FormSize.large}>
        <PostFormTooltipGlobalStyles />
        <PostFormContext.Provider value={formContextValue}>
          <FlushRegistryContext.Provider value={flushRegistryValue}>
            {confirmDialog}
            {formContextValue.postFormValue && (
              <div ref={ref} className="flex min-h-0 min-w-0 flex-grow">
                <PostFormTemplate
                  formTitle={props.formTitle}
                  renderAccountsSelector={renderAccountsSelector}
                  isContentLabelFeatureEnabled={isContentLabelFeatureEnabled}
                  contentLabelOptions={contentLabelOptions}
                  selectedContentLabel={selectedContentLabel}
                  isFetchingAccountGroups={isFetchingAccountGroups}
                  bulkTab={props.bulkTab}
                >
                  {isSinglePlatform ? (
                    <SinglePlatformPostForm
                      post={props.post}
                      postFormValue={formContextValue.postFormValue}
                      errors={props.errors}
                      warnings={props.warnings}
                      platforms={props.platforms}
                      platformFields={props.platformFields}
                      formAccounts={props.formAccounts}
                      updateFields={props.updateFields}
                      updateField={props.updateField}
                      platformPost={props.platformPost}
                      selectedPlatform={props.selectedPlatform}
                      visibleFields={props.visibleFields}
                      isSummary={props.isSummary}
                      hasPostTags={hasPostTags}
                      hasTikTokPosting={hasTikTokPosting}
                      oldTiktokAccounts={oldTiktokAccounts}
                      ttIsNotification={ttIsNotification}
                      hasThreadsPosting={hasThreadsPosting}
                      oldThreadsAccounts={oldThreadsAccounts}
                      thIsNotification={thIsNotification}
                      showUrlField={showUrlField}
                      bulkTab={props.bulkTab}
                      onCoverPreviewChange={handleCoverPreviewChange}
                      captionEditHistory={captionHistoryMap.get(
                        props.selectedPlatform
                      )}
                      selectedContentLabel={selectedContentLabel}
                      accountGroupId={selectedAccountGroupId}
                    />
                  ) : (
                    <ClusterPostForm
                      post={props.post}
                      postFormValue={formContextValue.postFormValue}
                      errors={props.errors}
                      warnings={props.warnings}
                      platforms={props.platforms}
                      platformFields={props.platformFields}
                      formAccounts={props.formAccounts}
                      updateFields={props.updateFields}
                      updateField={props.updateField}
                      platformPost={props.platformPost}
                      selectedPlatform={props.selectedPlatform}
                      visibleFields={props.visibleFields}
                      isSummary={props.isSummary}
                      hasPostTags={hasPostTags}
                      hasTikTokPosting={hasTikTokPosting}
                      oldTiktokAccounts={oldTiktokAccounts}
                      ttIsNotification={ttIsNotification}
                      hasThreadsPosting={hasThreadsPosting}
                      oldThreadsAccounts={oldThreadsAccounts}
                      thIsNotification={thIsNotification}
                      showUrlField={showUrlField}
                      bulkTab={props.bulkTab}
                      onCoverPreviewChange={handleCoverPreviewChange}
                      captionEditHistory={captionHistoryMap.get(
                        props.selectedPlatform
                      )}
                      selectedContentLabel={selectedContentLabel}
                      accountGroupId={selectedAccountGroupId}
                    />
                  )}
                </PostFormTemplate>
              </div>
            )}
          </FlushRegistryContext.Provider>
        </PostFormContext.Provider>
      </FormThemeProvider>
    </BugsnagReactReports>
  );
}

export default PostForm;
